import { apiClient } from "../../../services/api";
import { getRoomPathname } from "../../room/utils/getRoomPathname";
import { waitForHomeDestination } from "../../app/hooks/useHomeSurface";
import type { CurrentUser } from "../hooks/useCurrentUser";

/**
 * Where to send a user from a caller with no React context — the post-login
 * exit, a notification tap, the room-preview OAuth callback.
 *
 * The pinned room is not the answer on its own any more: a per-room GrowthBook
 * rule can send a room's members to JuisciGPT instead, and that answer is only
 * knowable once the feature payload has landed. `waitForHomeDestination` reads
 * it from the single reactive read `useHomeSurface` publishes, bounded by its
 * own deadline — never `growthbook.isOn()` here, and never an unbounded await
 * on the payload (`.knowledge/growthbook-oneshot-flag-race.md`).
 */
export async function getUserStartingPage(user: {
  uid: string;
  pinnedRoom: string | null;
  roomsOwner: CurrentUser["roomsOwner"];
}): Promise<string> {
  // The B2C home, and the fallback for a room the user cannot read:
  // mirror `getInitialRoute` so the root `/` redirect and the
  // post-login/notification navigation agree.
  const noPinHome = "/gpt/chat/new";

  const destination = await waitForHomeDestination(user);
  if (destination.kind === "gpt") return noPinHome;

  try {
    await apiClient.get("/webapp/private/rooms/:id/type", {
      param: { id: destination.roomId },
    });
  } catch {
    return noPinHome;
  }

  return getRoomPathname(destination.roomId);
}
